/** @format */

import { createContext, useEffect, useReducer, type ReactNode } from 'react';
import Keycloak from 'keycloak-js';
import SplashScreen from 'src/components/SplashScreen';
import { keycloakConfig } from 'src/config';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import i18n from 'src/i18n';
import { applyLoginLocale, clearManualLanguageSelection } from 'src/utils/locale';
import { setUserData } from 'src/slices/users';
import { useDispatch } from 'src/store';
import {
  clearKeycloakLoginTracked,
  getGa4PageContext,
  getGa4UserContext,
  markKeycloakLoginTracked,
  shouldTrackKeycloakLogin,
  trackGa4
} from 'src/analytics';
import type { AuthState, User, UserInfoResponse } from 'src/types';

export let keycloakClient = new Keycloak({
  ...keycloakConfig
});

/** Strip hash so OAuth callback fragments (#code=...) are never reused as redirect_uri. */
export function getKeycloakRedirectUri(href = window.location.href): string {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}${url.search}`;
  } catch (_) {
    return window.location.origin + '/';
  }
}

let keycloakInitPromise: Promise<boolean> | null = null;
/** Shared user/info request — StrictMode remounts AuthProvider but must not refetch. */
let userInfoRequest: Promise<User> | null = null;

/** Reset singleton init guard (tests / full page remount scenarios). */
export function resetKeycloakInitPromise(): void {
  keycloakInitPromise = null;
  userInfoRequest = null;
}

type AuthAction =
  | {
      type: 'INITIALISE';
      payload: { user: User | null; uuid?: string; isAuthenticated: boolean };
    }
  | {
      type: 'LOGIN';
      payload: {
        user: User;
        role?: string | null;
        user_status?: string;
        uuid?: string;
        email?: string | null;
      };
    }
  | { type: 'LOGOUT' }
  | {
      type: 'ON_ERROR';
      payload: { error: { message: string; err?: unknown }; isAuthenticated?: boolean; user?: null };
    }
  | { type: 'UPDATE_ONBOARDING_COMPLETE' };

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  role: null,
  error: null,
  email: null
};

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INITIALISE': {
      const { user, uuid, isAuthenticated } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        uuid
      };
    }
    case 'LOGIN': {
      const { user, role, user_status, uuid, email } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        isInitialised: true,
        user,
        user_status,
        role: role,
        uuid: uuid,
        email
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'ON_ERROR': {
      return {
        ...state,
        isInitialised: true,
        isAuthenticated: false,
        user: null,
        error: action.payload.error
      };
    }
    case 'UPDATE_ONBOARDING_COMPLETE': {
      return {
        ...state,
        user: state.user ? { ...state.user, onBoardingComplete: true } : state.user
      };
    }
    default: {
      return { ...state };
    }
  }
};

export interface AuthContextValue extends AuthState {
  logout?: () => void;
  updateOnBoardingComplete?: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState
});

export interface AuthProviderProps {
  children?: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const storeDispatch = useDispatch();

  const logout = () => {
    clearManualLanguageSelection();
    clearKeycloakLoginTracked();
    // Always return to home after logout. Using the init-time / current deep link
    // as post_logout_redirect_uri can land on RoleGuard or catch-all → /404.
    keycloakClient.logout({ redirectUri: `${window.location.origin}/` });
  };

  const getUserInfo = async (
    roles: string[],
    uuid: string | undefined,
    email: string | undefined,
    { trackLogin = false }: { trackLogin?: boolean } = {}
  ) => {
    // Reuse in-flight/resolved payload so StrictMode double-mount hits the network once,
    // while each AuthProvider instance still applies LOGIN to its own reducer.
    if (!userInfoRequest) {
      userInfoRequest = axios.get<UserInfoResponse>(`${ENDPOINTS.GET_USER_INFO_ENDPOINT}`).then(
        response => response.data,
        err => {
          userInfoRequest = null;
          throw err;
        }
      );
    }

    try {
      const user = await userInfoRequest;
      const onBoardingComplete = user.onBoardingComplete ?? user.onboardingComplete ?? false;
      const preferred_username =
        user.preferred_username ?? keycloakClient.tokenParsed?.preferred_username;
      const userWithOnboarding = { ...user, email, preferred_username, onBoardingComplete };
      dispatch({
        type: 'LOGIN',
        payload: {
          user: userWithOnboarding,
          role: roles[0],
          uuid
        }
      });

      storeDispatch(setUserData(userWithOnboarding));

      await applyLoginLocale(user.locale);

      if (trackLogin && shouldTrackKeycloakLogin()) {
        trackGa4(
          'login',
          {},
          {
            pageContext: getGa4PageContext(window.location.pathname),
            userContext: getGa4UserContext({ isAuthenticated: true, uuid })
          }
        );
        markKeycloakLoginTracked();
      }
    } catch (err) {
      const isServiceUnavailable = (err as { response?: { status?: number } })?.response?.status === 503;

      dispatch({
        type: 'ON_ERROR',
        payload: {
          isAuthenticated: false,
          user: null,
          error: {
            message: isServiceUnavailable
              ? 'User info service is temporarily unavailable (503). Please try again later.'
              : 'Error while trying to fetch user info, please try again',
            err
          }
        }
      });
    }
  };

  const updateOnBoardingComplete = () => {
    dispatch({ type: 'UPDATE_ONBOARDING_COMPLETE' });
  };

  useEffect(() => {
    const initialise = async () => {
      const trackLoginEvent = shouldTrackKeycloakLogin();

      try {
        keycloakClient.onTokenExpired = async () => {
          const refreshed = await keycloakClient.updateToken();
          if (refreshed) {
            axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
          }
        };
        keycloakClient.onAuthRefreshError = () => {
          window.location.href = '/';
        };
        keycloakClient.onAuthError = () => {
          window.location.href = '/';
        };

        keycloakClient.onAuthSuccess = () => {};
        keycloakClient.onReady = () => {};
        keycloakClient.onAuthLogout = () => {};
        keycloakClient.onAuthRefreshSuccess = () => {};

        // StrictMode remounts in React 19; keycloak-js must only init once per page load.
        if (!keycloakInitPromise) {
          keycloakInitPromise = keycloakClient.init({
            onLoad: 'login-required',
            checkLoginIframe: false,
            silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
            redirectUri: getKeycloakRedirectUri(),
            pkceMethod: 'S256',
            locale: i18n.language || 'en'
          } as Keycloak.KeycloakInitOptions);
        }

        await keycloakInitPromise;

        if (keycloakClient.authenticated) {
          axios.defaults.headers.common.Authorization = `Bearer ${keycloakClient.token}`;
          const keyClockAccessRoles =
            ((keycloakClient.idTokenParsed as { roles?: string[] } | undefined)?.roles as string[]) || [];
          await getUserInfo(keyClockAccessRoles, keycloakClient.subject, keycloakClient.tokenParsed?.email, {
            trackLogin: trackLoginEvent
          });
        }
      } catch (err) {
        keycloakInitPromise = null;
        dispatch({
          type: 'ON_ERROR',
          payload: {
            isAuthenticated: false,
            user: null,
            error: {
              message: 'Error while trying to log in, please try again',
              err
            }
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout,
        updateOnBoardingComplete
      }}>
      {state.isAuthenticated || state.error ? children : <></>}
    </AuthContext.Provider>
  );
};

export default AuthContext;
